/**
 * OrtoSoft — landing, route "/"
 *
 * THESIS: the portfolio as a drafting sheet. The brand promise — "The right
 * angle." — is made literal: each work is presented as a constructed drawing
 * annotated with real dimensions. Refuses the dark-hero / gradient-text /
 * card-grid developer-portfolio default.
 *
 * OWN-WORLD: millimetre paper #FAF9F5 under an 8px blue grid; ink #191C20;
 * drafting red #D9482B as line and structure only (margin, active state, 90°
 * mark); technical blue #2456A6 for dimensions. Saira Condensed caps for
 * display and labels, Saira for body. Zero radius, flat, no shadows.
 *
 * STORY: a visitor vetting a builder sees construction before product, reads
 * two real drawings at three distances (purpose / in action / the making),
 * and leaves with a contact route.
 *
 * FIRST VIEWPORT: sheet front — the O mark drafted (square, diagonals, ring
 * with aperture, red 90° corner mark, blue Ø dimension); "THE RIGHT ANGLE."
 * with a 90° dimension line; drafting notes block; primary action
 * "Voir les dessins". Red margin line at the sheet's left edge.
 *
 * FORM: one sheet, two product drawings, each staged at three fixed viewing
 * distances via a segmented stepper, opening at the "overview" distance.
 * Staging: reveal-transform-three-distances. Seed: 89d1d582 (scope direction,
 * mode experience). Copy is served in the browser's language (fr / en) via
 * src/i18n.
 */
import { useState } from 'react';
import './App.css';
import Header from './header';
import Footer from './footer';
import OConstruction from './OConstruction';
import { useI18n } from './i18n';
import { telHref, PHONE_DISPLAY } from './phone';
import extPopupImg from './img/extension1.JPG';
import extOptionsImg from './img/extension2.JPG';
import orthoImg from './img/ortho.jpg';
import videoImg from './img/ VideoDemo.jpg';
import bookmarksImg from './img/bookmarks.png';

const STORE_URL = 'https://chromewebstore.google.com/detail/doctolib-pro-export-agend/hhmihfcfknlgddecdopnconfobegafca';

function Sheet({ index, title, sub, across, facts, plates, url, demoUrl, primaryLabel }) {
  const { t } = useI18n();
  const [d, setD] = useState('vue');
  const distances = [
    { id: 'vue', label: t('dist.overview') },
    { id: 'action', label: t('dist.action') },
    { id: 'capot', label: t('dist.hood') },
  ];
  return (
    <article className="os-sheet" aria-labelledby={`sheet-${index}`}>
      <div className="os-sheet-head">
        <span className="os-tab">{t('sheet.tab')} {index}</span>
        <h3 className="os-sheet-title" id={`sheet-${index}`}>{title}</h3>
        <p className="os-sheet-sub">{sub}</p>
      </div>
      <div className="os-dist" role="tablist" aria-label={`${title} — ${t('sheet.reading')}`}>
        {distances.map((dist) => (
          <button
            key={dist.id}
            type="button"
            role="tab"
            aria-selected={d === dist.id}
            className={'os-dist-btn' + (d === dist.id ? ' is-active' : '')}
            onClick={() => setD(dist.id)}
          >
            {dist.label}
          </button>
        ))}
      </div>
      <div className="os-distbody" role="tabpanel" key={d}>
        {d === 'vue' && (
          <div>
            <p className="os-across-line">{across}</p>
            <a className="os-btn os-across-url" href={url} target="_blank" rel="noreferrer">
              {t('sheet.visit')} — {url.replace('https://', '')} <span className="os-arrow" aria-hidden="true">→</span>
            </a>
          </div>
        )}
        {d === 'action' && (
          <div>
            <div className="os-plates">
              {plates.map((p) => (
                <figure className="os-plate" key={p.tab}>
                  {p.href ? (
                    <a href={p.href} target="_blank" rel="noreferrer">
                      <img src={p.src} alt={p.alt} />
                    </a>
                  ) : (
                    <img src={p.src} alt={p.alt} />
                  )}
                  <figcaption className="os-plate-cap">
                    <span>{p.tab}</span>
                    <span>{p.cap}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="os-action-btns">
              <a className="os-btn os-btn-primary" href={url} target="_blank" rel="noreferrer">
                {primaryLabel || t('sheet.login')}
              </a>
              {demoUrl && (
                <a className="os-btn" href={demoUrl} target="_blank" rel="noreferrer">
                  {t('sheet.demo')} <span aria-hidden="true">→</span>
                </a>
              )}
            </div>
          </div>
        )}
        {d === 'capot' && (
          <dl className="os-facts">
            {facts.map(([k, v]) => (
              <div className="os-facts-row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </article>
  );
}

export default function App() {
  const { t } = useI18n();
  return (
    <div className="os-page">
      <Header />
      <main className="os-main">
        {/* sheet front */}
        <section className="os-hero" aria-labelledby="os-title">
          <div className="os-o-wrap">
            <OConstruction />
          </div>
          <div>
            <p className="os-kicker">{t('hero.kicker')}</p>
            <h1 className="os-title" id="os-title">The right angle.</h1>
            <div className="os-dim90" aria-hidden="true">
              <span className="os-dim-line"></span>
              <span className="os-dim-label">90°</span>
              <span className="os-dim-line"></span>
            </div>
            <p className="os-lede">{t('hero.lede')}</p>
            <div className="os-hero-cta">
              <a className="os-btn os-btn-primary" href="#dessins">{t('hero.ctaDrawings')}</a>
              <a className="os-btn" href="mailto:contact@ortosoft.fr">{t('hero.ctaContact')}</a>
            </div>
            <dl className="os-notes">
              <div><dt>{t('note.scale')}</dt><dd>1 : 1</dd></div>
              <div><dt>{t('note.author')}</dt><dd>B. Scanteie</dd></div>
              <div><dt>{t('note.sheets')}</dt><dd>3</dd></div>
              <div><dt>{t('note.year')}</dt><dd>2026</dd></div>
            </dl>
          </div>
        </section>

        {/* the drawings */}
        <section className="os-section" id="dessins" aria-labelledby="h-dessins">
          <div className="os-section-head">
            <span className="os-section-index">{t('section.series')} 01</span>
            <h2 className="os-section-title" id="h-dessins">{t('section.drawings')}</h2>
            <span className="os-section-rule" aria-hidden="true"></span>
          </div>

          <Sheet
            index="01"
            title={t('p01.title')}
            sub={t('p01.sub')}
            across={t('p01.across')}
            url="https://ortho.ortosoft.fr"
            demoUrl="https://youtu.be/J9-8GfLD4ns"
            plates={[
              { src: orthoImg, alt: t('p01.capTracing'), tab: `${t('plate.fig')} 01A`, cap: t('p01.capTracing') },
              { src: videoImg, alt: t('p01.capDemo'), href: 'https://youtu.be/J9-8GfLD4ns', tab: `${t('plate.fig')} 01B`, cap: t('p01.capDemo') },
            ]}
            facts={[
              [t('p01.f1k'), t('p01.f1v')],
              [t('p01.f5k'), t('p01.f5v')],
              [t('p01.f6k'), t('p01.f6v')],
              [t('p01.f2k'), t('p01.f2v')],
              [t('p01.f3k'), t('p01.f3v')],
              [t('p01.f4k'), t('p01.f4v')],
            ]}
          />

          <Sheet
            index="02"
            title={t('p02.title')}
            sub={t('p02.sub')}
            across={t('p02.across')}
            url="https://sb.ortosoft.fr"
            plates={[
              { src: bookmarksImg, alt: t('p02.title'), tab: `${t('plate.fig')} 02`, cap: t('p02.capLive') },
            ]}
            facts={[
              [t('p02.f1k'), t('p02.f1v')],
              [t('p02.f2k'), t('p02.f2v')],
              [t('p02.f3k'), t('p02.f3v')],
            ]}
          />

          <Sheet
            index="03"
            title={t('p03.title')}
            sub={t('p03.sub')}
            across={t('p03.across')}
            url={STORE_URL}
            primaryLabel={t('sheet.get')}
            plates={[
              { src: extPopupImg, alt: t('p03.title'), href: STORE_URL, tab: `${t('plate.fig')} 03A`, cap: t('p03.capPopup') },
              { src: extOptionsImg, alt: t('p03.title'), href: STORE_URL, tab: `${t('plate.fig')} 03B`, cap: t('p03.capOptions') },
            ]}
            facts={[
              [t('p03.f1k'), t('p03.f1v')],
              [t('p03.f2k'), t('p03.f2v')],
              [t('p03.f3k'), t('p03.f3v')],
            ]}
          />
        </section>

        {/* the drafter */}
        <section className="os-section" id="contact" aria-labelledby="h-contact">
          <div className="os-section-head">
            <span className="os-section-index">{t('section.series')} 02</span>
            <h2 className="os-section-title" id="h-contact">{t('section.drafter')}</h2>
            <span className="os-section-rule" aria-hidden="true"></span>
          </div>
          <div className="os-about-body">
            <p className="os-about-line">{t('about.line')}</p>
            <p className="os-about-tag">{t('about.tag')}</p>
            <div className="os-hero-cta">
              <a className="os-btn os-btn-primary" href="mailto:contact@ortosoft.fr">
                {t('about.write')} — contact@ortosoft.fr
              </a>
              <a className="os-btn" href={telHref}>
                {t('about.call')} — {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
